import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import styles from "./examMarkEntry.module.css";
import AxiosInstance, { TokenService } from "../../../services/Api";
import {
  CustomButton,
  CustomDropdown,
  CustomFileInput,
} from "../../../components";
import { fetchStaffClassAndSubjects } from "../../../redux/Masterdata";
import dayjs from "dayjs";
import PopupModal from "../../../components/PopupModal/PopupModal";

const MarkBulkUpload = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { translations } = useSelector((state) => state.language);
  const user = TokenService.getUser();
  const clientData = user?.client_data[0] || {};
  const school_id = clientData?.school_id || "";
  const staff_id = clientData?.master_id;
  const academic_year_id = clientData?.academic_year_id || "";

  // ─── Dropdown selections ───
  const [selectedClass, setSelectedClass] = useState(null);
  const [selectedSection, setSelectedSection] = useState(null);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [selectedExamName, setSelectedExamName] = useState(null);
  const [selectedModeOfExam, setSelectedModeOfExam] = useState(null);

  // ─── Exam config data ───
  const [examData, setExamData] = useState([]);

  // ─── Form state ───
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);

  // ─── Download & Upload flow state ───
  const [isDownloaded, setIsDownloaded] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  // ─── Modal state ───
  const [modalState, setModalState] = useState({
    open: false,
    type: "success",
    message: "",
    redirectPath: "",
  });

  // ─── Redux class options ───
  const { staffClassAndSubjectOptions: reduxClassOptions } = useSelector(
    (state) => state.masterData,
  );

  // ─── Exam dropdown options ───
  const examOptions = examData.map((item) => ({
    label: item.exam_type,
    value: item.id,
  }));

  // ─── Class dropdown (deduplicated) ───
  const classOptions = useMemo(() => {
    const seen = new Set();
    return (reduxClassOptions || [])
      .filter((item) => {
        const key = String(item.class_id);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .map((item) => ({
        value: String(item.class_id),
        label: item.class_name,
      }));
  }, [reduxClassOptions]);

  // ─── Section dropdown (filtered by selected class, deduplicated) ───
  const sectionOptions = useMemo(() => {
    if (!selectedClass) return [];
    const classId =
      typeof selectedClass === "object" ? selectedClass?.value : selectedClass;
    const seen = new Set();
    return (reduxClassOptions || [])
      .filter((item) => {
        if (String(item.class_id) !== String(classId)) return false;
        const key = String(item.section_id);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .map((item) => ({
        value: String(item.section_id),
        label: String(item.section_id),
      }));
  }, [reduxClassOptions, selectedClass]);

  // ─── Subject dropdown (filtered by selected class & section, deduplicated) ───
  const subjectOptions = useMemo(() => {
    if (!selectedClass) return [];
    const classId =
      typeof selectedClass === "object" ? selectedClass?.value : selectedClass;
    const sectionId =
      typeof selectedSection === "object"
        ? selectedSection?.value
        : selectedSection;

    const seen = new Set();
    return (reduxClassOptions || [])
      .filter((item) => {
        if (String(item.class_id) !== String(classId)) return false;
        if (sectionId && String(item.section_id) !== String(sectionId))
          return false;
        if (seen.has(item.subject_id)) return false;
        seen.add(item.subject_id);
        return true;
      })
      .map((item) => ({
        value: item.subject_id,
        label: item.subject_name,
      }));
  }, [reduxClassOptions, selectedClass, selectedSection]);

  // ─── Mode of Exam options ───
  const passRulesOptions = [
    {
      value: "Theory",
      label: translations?.labels?.theory || "Theory",
      disabled: false,
    },
    {
      value: "Practical",
      label: translations?.labels?.practical || "Practical",
      disabled: false,
    },
  ];

  // ─── FIX: Fetch ALL staff class & subjects ONCE on mount ───
  // Pass empty strings so the API returns the full unfiltered list.
  // Local filtering is handled by the useMemo hooks above.
  useEffect(() => {
    dispatch(
      fetchStaffClassAndSubjects({
        staff_id: staff_id,
        class_id: "",
        section_id: "",
        subject_id: "",
      }),
    );
  }, [dispatch, staff_id]);

  // ─── Fetch exam config list on mount ───
  useEffect(() => {
    fetchExamConfigList();
  }, []);

  const fetchExamConfigList = async () => {
    try {
      const payload = {
        school_id: parseInt(school_id),
        academic_year: parseInt(academic_year_id),
      };

      const response = await AxiosInstance.post(
        `/exam/examConfig/list`,
        payload,
      );

      if (response?.data?.status) {
        const data = response.data.data || [];
        const transformedData = data.map((item, index) => ({
          id: item.id,
          sno: index + 1,
          academic_id: item.academic_id,
          academic_year: item.academic_year,
          exam_type: item.exam_type,
          start_date: item.start_date
            ? dayjs(item.start_date).format("DD-MM-YYYY")
            : "",
          end_date: item.end_date
            ? dayjs(item.end_date).format("DD-MM-YYYY")
            : "",
          isActive: item.isActive,
        }));
        setExamData(transformedData);
      } else {
        setExamData([]);
      }
    } catch (error) {
      console.error("Error fetching exam config list:", error);
    }
  };

  // ─── Dropdown change handlers (cascade reset child dropdowns) ───
  const handleClassChange = (selected) => {
    setSelectedClass(selected);
    setSelectedSection(null);
    setSelectedSubject(null);
    setErrors((prev) => ({ ...prev, class: "", section: "", subject: "" }));
  };

  const handleSectionChange = (selected) => {
    setSelectedSection(selected);
    setSelectedSubject(null);
    setErrors((prev) => ({ ...prev, section: "", subject: "" }));
  };

  const handleSubjectChange = (selected) => {
    setSelectedSubject(selected);
    setErrors((prev) => ({ ...prev, subject: "" }));
  };

  const handleExamNameChange = (selected) => {
    setSelectedExamName(selected);
    setErrors((prev) => ({ ...prev, examName: "" }));
  };

  const handleModeOfExamChange = (selected) => {
    setSelectedModeOfExam(selected);
    setErrors((prev) => ({ ...prev, modeofexam: "" }));
  };

  // ─── File change handler ───
  const handleFileChange = (e) => {
    const file = e?.target?.files?.[0] || e;
    if (file) {
      const allowedTypes = [
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.ms-excel",
      ];
      if (!allowedTypes.includes(file.type)) {
        setErrors((prev) => ({
          ...prev,
          file:
            translations?.labels?.onlyExcelFilesAllowed ||
            "Only Excel files (.xlsx) are allowed",
        }));
        return;
      }
      setSelectedFile(file);
      setErrors((prev) => ({ ...prev, file: "" }));
    }
  };

  // ─── Validation ───
  const validateForm = () => {
    const newErrors = {};
    if (!selectedClass)
      newErrors.class =
        translations?.labels?.classRequired || "Class is required";
    if (!selectedSection)
      newErrors.section =
        translations?.labels?.sectionRequired || "Section is required";
    if (!selectedSubject)
      newErrors.subject =
        translations?.labels?.subjectRequired || "Subject is required";
    if (!selectedExamName)
      newErrors.examName =
        translations?.labels?.examNameRequired || "Exam Name is required";
    if (!selectedModeOfExam)
      newErrors.modeofexam =
        translations?.labels?.modeOfExamRequired || "Mode of Exam is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ─── DOWNLOAD EXCEL (frameset API) ───
  const handleDownload = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      const payload = {
        staff_id: parseInt(staff_id),
        config_id: parseInt(selectedExamName?.value),
        school_id: parseInt(school_id),
        class_id: String(selectedClass?.value),
        section_id: String(selectedSection?.value),
        subject_id: parseInt(selectedSubject?.value),
        mode_exam: String(selectedModeOfExam?.value),
      };

      const response = await AxiosInstance.post(`/exam/frameset`, payload);

      if (response?.data?.status) {
        // Normalize the API value (relative path, or absolute URL from an
        // older backend) to a path, then download from the CURRENT host —
        // base derived from the axios baseURL the API calls already use.
        // Fetching via AxiosInstance sends the Tenant-ID/auth headers, so
        // the file saves locally without a header-less navigation.
        const rawFileUrl = response.data.data || "";
        let filePath = rawFileUrl;
        if (/^https?:\/\//i.test(rawFileUrl)) {
          try {
            filePath = new URL(rawFileUrl).pathname;
          } catch (e) {
            /* keep as-is */
          }
        }
        filePath = filePath.replace(/^\/?api\//i, "/");
        const apiBase = AxiosInstance.defaults.baseURL || "";
        const staticBase = apiBase.replace(/\/v1\/api\/?$/i, "");
        const fullUrl = `${staticBase.replace(/\/$/, "")}/${filePath.replace(/^\//, "")}`;
        const fileName = filePath.split("/").pop() || "Exam_Frame.xlsx";

        const fileRes = await AxiosInstance.get(fullUrl, {
          responseType: "blob",
        });
        const objectUrl = URL.createObjectURL(
          new Blob([fileRes.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          }),
        );
        const link = document.createElement("a");
        link.href = objectUrl;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(objectUrl);

        // Mark as downloaded — disable dropdowns & show upload section
        setIsDownloaded(true);
      } else {
        setModalState({
          open: true,
          type: "error",
          message:
            response?.data?.message ||
            translations?.labels?.downloadFailed ||
            "Failed to download Excel. Please try again.",
          redirectPath: "",
        });
      }
    } catch (error) {
      console.error("Error downloading frameset:", error);
      setModalState({
        open: true,
        type: "error",
        message:
          translations?.labels?.downloadFailed ||
          "Failed to download Excel. Please try again.",
        redirectPath: "",
      });
    } finally {
      setLoading(false);
    }
  };

  // Map known bulk-upload API messages to translated labels.
  // e.g. "Marks already exist at row 2" -> "Marks already exist" (row dropped).
  // Unknown messages are shown as-is; empty falls back to the generic error.
  const getUploadErrorMessage = (apiMessage) => {
    const labels = translations?.labels || {};
    const fallback =
      labels.markBulkUploadFailed ||
      "Failed to upload marks. Please try again.";
    if (!apiMessage) return fallback;

    if (/marks already exist/i.test(apiMessage)) {
      return labels.marksAlreadyExist || "Marks already exist";
    }

    return apiMessage;
  };

  // ─── UPLOAD EXCEL (bulk-upload API) ───
  const handleUpload = async () => {
    if (!selectedFile) {
      setErrors((prev) => ({
        ...prev,
        file:
          translations?.labels?.pleaseSelectExcelFile ||
          "Please select an Excel file to upload.",
      }));
      return;
    }

    setUploadLoading(true);
    try {
      const formData = new FormData();
      formData.append("mark_entry_file", selectedFile);

      const response = await AxiosInstance.post(
        `/exam/examMarkEntry/bulk-upload`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      if (response?.data?.status) {
        setModalState({
          open: true,
          type: "success",
          message:
            response?.data?.message ||
            translations?.labels?.markBulkUploadSuccess ||
            "Marks uploaded successfully!",
          redirectPath: "back",
        });
      } else {
        setModalState({
          open: true,
          type: "error",
          message: getUploadErrorMessage(response?.data?.message),
          redirectPath: "",
        });
      }
    } catch (error) {
      console.error("Error uploading marks:", error);
      // Non-2xx responses: the API body lives on error.response.data
      setModalState({
        open: true,
        type: "error",
        message: getUploadErrorMessage(
          error?.response?.data?.message || error?.data?.message,
        ),
        redirectPath: "",
      });
    } finally {
      setUploadLoading(false);
    }
  };

  // ─── Cancel / Reset ───
  const handleCancel = () => {
    setSelectedClass(null);
    setSelectedSection(null);
    setSelectedSubject(null);
    setSelectedExamName(null);
    setSelectedModeOfExam(null);
    setSelectedFile(null);
    setIsDownloaded(false);
    setErrors({});
    navigate(-1);
  };

  // ─── Modal OK handler ───
  const handleSuccessOk = () => {
    const shouldGoBack = modalState.redirectPath === "back";
    setModalState({
      open: false,
      type: "success",
      message: "",
      redirectPath: "",
    });
    if (shouldGoBack) {
      navigate(-1);
    }
  };

  return (
    <>
      <div className={styles.container}>
        {/* ─── Header ─── */}
        <div className={styles.header}>
          <h1 className={styles.headerTitle}>
            {translations?.labels?.markBulkUpload || "Mark Bulk Upload"}
          </h1>
        </div>

        {/* ─── Filter Dropdowns ─── */}
        <div className="row g-3 align-items-center p-3">
          {/* Class */}
          <div className="col-lg-3 px-2 col-md-6 col-sm-12">
            <CustomDropdown
              label={translations?.labels?.class || "Class"}
              name="classValue"
              value={selectedClass}
              onChange={handleClassChange}
              options={classOptions}
              placeholder={translations?.labels?.selectClass || "Select Class"}
              required
              error={errors.class}
              isSearchable={false}
              disabled={isDownloaded}
            />
          </div>

          {/* Section */}
          <div className="col-lg-3 px-2 col-md-6 col-sm-12">
            <CustomDropdown
              label={translations?.labels?.section || "Section"}
              name="section"
              value={selectedSection}
              options={sectionOptions}
              onChange={handleSectionChange}
              placeholder={
                translations?.labels?.selectSection || "Select Section"
              }
              required
              error={errors.section}
              isSearchable={false}
              disabled={isDownloaded}
            />
          </div>

          {/* Subject */}
          <div className="col-lg-3 px-2 col-md-6 col-sm-12">
            <CustomDropdown
              label={translations?.labels?.subjects || "Subjects"}
              name="subject"
              value={selectedSubject}
              options={subjectOptions}
              onChange={handleSubjectChange}
              placeholder={
                translations?.labels?.selectsubjectPlaceholder ||
                "Select Subject"
              }
              required
              error={errors.subject}
              isSearchable={false}
              disabled={isDownloaded}
            />
          </div>

          {/* Exam Name */}
          <div className="col-lg-3 px-2 col-md-6 col-sm-12">
            <CustomDropdown
              label={translations?.labels?.examName || "Exam Name"}
              name="examName"
              value={selectedExamName}
              onChange={handleExamNameChange}
              options={examOptions}
              placeholder={
                translations?.labels?.selectExamName || "Select Exam Name"
              }
              required
              error={errors.examName}
              isSearchable={false}
              disabled={isDownloaded}
            />
          </div>

          {/* Mode of Exam */}
          <div className="col-lg-3 px-2 col-md-6 col-sm-12">
            <CustomDropdown
              label={translations?.labels?.modeOfExam || "Mode of Exam"}
              name="modeofexam"
              value={selectedModeOfExam}
              onChange={handleModeOfExamChange}
              options={passRulesOptions}
              placeholder={
                translations?.labels?.selectModeOfExam || "Select Mode"
              }
              required
              error={errors.modeofexam}
              isSearchable={false}
              disabled={isDownloaded}
            />
          </div>

          {/* Download & Cancel Buttons (visible before download) */}
          {!isDownloaded && (
            <>
              <div className="col-lg-3 px-2 col-md-6 col-sm-12 mt-3">
                {/* <div className="d-flex gap-2"> */}
                <CustomButton
                  variant="contained"
                  onClick={handleDownload}
                  className={styles.saveButton}
                  loading={loading}
                >
                  {translations?.labels?.downloadExcel || "DOWNLOAD EXCEL"}
                </CustomButton>
              </div>
              <div className="col-lg-3 px-2 col-md-6 col-sm-12 mt-3">
                <CustomButton
                  variant="contained"
                  onClick={handleCancel}
                  className={styles.cancelButton}
                  disabled={loading}
                >
                  {translations?.labels?.cancel || "CANCEL"}
                </CustomButton>
                {/* </div> */}
              </div>
            </>
          )}
        </div>

        {/* ─── Upload Section (visible after download) ─── */}
        {isDownloaded && (
          <div className="row g-3 p-3">
            <div className="col-lg-6 col-md-8 col-sm-12">
              <CustomFileInput
                label={
                  translations?.labels?.uploadMarksheet || "Upload Marksheet"
                }
                name="markEntryFile"
                value={selectedFile}
                onChange={handleFileChange}
                accept=".xlsx,.xls"
                placeholder={
                  selectedFile
                    ? selectedFile.name
                    : translations?.labels?.noFileChosen || "No file chosen"
                }
                buttonText={translations?.labels?.chooseFile || "Choose File"}
                error={errors.file}
              />
            </div>

            <div className="col-md-4 px-2 d-flex align-items-end">
              <div className="d-flex gap-2">
                <CustomButton
                  variant="contained"
                  onClick={handleUpload}
                  className={styles.saveButton}
                  loading={uploadLoading}
                >
                  {translations?.labels?.upload || "UPLOAD"}
                </CustomButton>

                <CustomButton
                  variant="contained"
                  onClick={handleCancel}
                  className={styles.cancelButton}
                  disabled={uploadLoading}
                >
                  {translations?.labels?.cancel || "CANCEL"}
                </CustomButton>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ─── Popup Modal ─── */}
      <PopupModal
        open={modalState.open}
        title={modalState.type === "success" ? "Success" : "Failed"}
        message={modalState.message}
        onOk={handleSuccessOk}
      />
    </>
  );
};

export default MarkBulkUpload;
